import type { ScreenKey, ScreenHandle } from "@/lib/nav/screens";

/**
 * 来た画面の記録（PRODUCT_DEV-1043）
 *
 * 左上の「← ○○」を一律「その画面へ来る直前にいた画面」へ戻すための記録。
 * 戻り先を呼び出し側が `location.state.backTo` で渡す作りは、渡し忘れた入口（通知・手帳・
 * 日報の出典など）から入ると戻れなかった。SPA にはリファラーが無いので、**画面が切り替わるたびに
 * アプリ全体で 1 か所が記録する**。どちらの画面も戻り先を書かない。
 *
 * 記録の単位は「履歴の 1 枠」（`NavEntry`）。react-router の location.key に対応付けるが、
 * replace（保存後の遷移・リダイレクト・絞り込みの変更）では key が変わっても**同じ枠のまま**にする。
 * こうすると「戻り先の URL = その画面を離れたときの最新の URL」になり、絞り込みやタブが戻る。
 *
 * ← を押したときは、分かれば**履歴を戻る**（`navigate(-n)`）。新しい履歴を積まないので、
 * 詳細 → 一覧 → 戻る で詳細へ跳ね返る往復ループが起きず、ブラウザの「戻る」と同じ並びになる。
 *
 * この節は純関数（`reduceNav` / `resolveBack`）。ブラウザと router につなぐ部分は下の節。
 */

export interface ScreenOverride {
  url: string;
  title: string;
  /** 画面の中の状態を閉じたら来た画面へ戻る（ほかの画面から検索タブで開いた検索、など） */
  closeToOrigin?: boolean;
}

export interface NavEntry {
  id: string;
  /** 同じ番号の数え方が続いている範囲の印。ページを読み込み直して番号が振り直されたら変わる */
  doc: string;
  /** 履歴の位置（react-router が history.state に持つ idx）。分からなければ null */
  idx: number | null;
  /** その画面を最後に表示していたときの URL（basename を含む、そのまま navigate に渡せる形） */
  url: string;
  /** 同じ画面かどうかの判定に使う。画面の種類 + パス（クエリは含めない＝タブ・絞り込みは同じ画面） */
  identity: string;
  screen: ScreenKey | null;
  /** 戻り先にしない画面（録音中・作成中） */
  transient: boolean;
  /** 画面が記録したタイトル（商談名など）。← の ○○ に優先して出す */
  title?: string;
  /**
   * 画面の中で開いている状態ぶんの「戻ってくるときの行き先と名前」（商談一覧で検索を開いている、など）。
   * 履歴で戻って着いたあと、この URL へ置き換える。閉じたら消す
   */
  override?: ScreenOverride;
  /** 来た画面の枠 */
  from: string | null;
  /** 古いものから捨てるための通し番号 */
  seq: number;
}

export interface NavStore {
  /**
   * 記録したときのログイン（テナント + ユーザー）。違う人がログインしたら記録を捨てる。
   * 記録には商談名・顧客名などのタイトルが入るので、同じタブで次にログインした人に見せない
   */
  owner?: string;
  entries: Record<string, NavEntry>;
  /** location.key（の正規化したもの）→ 枠 */
  keys: Record<string, string>;
  seq: number;
}

export const emptyNavStore = (): NavStore => ({ entries: {}, keys: {}, seq: 0 });

export interface NavEvent {
  action: "PUSH" | "REPLACE" | "POP";
  /** 直前に表示していた画面の key（最初の 1 回は null） */
  prevKey: string | null;
  key: string;
  doc: string;
  idx: number | null;
  url: string;
  pathname: string;
  handle: ScreenHandle | null;
  /**
   * 戻る操作が URL で戻った（履歴を戻れなかった）とき、置き換え先はこの枠そのものとして扱う。
   * 来た画面の連なりと、手帳の日付の記憶（枠ごと）がそのまま続く。
   */
  adopt?: string;
  /**
   * メニュー・下のタブ・ロゴ・通知ベルから移った（PRODUCT_DEV-1159）。
   * このときは「A → B → A の往復を防ぐ決まり」をかけず、来た画面はひとつ前の画面にする
   */
  jump?: boolean;
}

/**
 * メニュー・下のタブなど外枠からの移動に付ける location.state の印（PRODUCT_DEV-1159）。
 * 往復を防ぐ決まりは保存・削除後に一覧や詳細へ自動で戻る移動のためのもので、
 * 人が外枠から選んで移ったときにかけると ← がひとつ前の画面にならない。
 */
export const NAV_JUMP_FIELD = "__navJump";

/** 外枠からの移動の location.state を作る（渡した state に印を足す） */
export const withNavJump = <T extends object>(state?: T | null): T & { [NAV_JUMP_FIELD]: true } =>
  ({ ...(state ?? {}), [NAV_JUMP_FIELD]: true }) as T & { [NAV_JUMP_FIELD]: true };

export const isNavJump = (state: unknown): boolean =>
  typeof state === "object" && state !== null && (state as Record<string, unknown>)[NAV_JUMP_FIELD] === true;

/** 記録しておく枠の上限。超えたら古いものから捨てる（捨てた枠を指す ← はホームへ） */
export const NAV_STORE_LIMIT = 200;

const identityOf = (handle: ScreenHandle | null, pathname: string): string =>
  `${handle?.screen ?? "?"}|${pathname}`;

const skipTransient = (store: NavStore, id: string | null): string | null => {
  let cur = id;
  const seen = new Set<string>();
  while (cur && store.entries[cur]?.transient && !seen.has(cur)) {
    seen.add(cur);
    cur = store.entries[cur].from;
  }
  return cur && store.entries[cur] ? cur : null;
};

/**
 * 進んだ先の「来た画面」を決める。
 *
 * - 同じ画面の中の移動（タブ・絞り込みを push で変えた）は来た画面に数えない（前の枠の来た画面を引き継ぐ）
 * - 録音中・作成中の画面は飛ばす
 * - A → B → A と**進む操作で戻ってきた**（保存して一覧へ、など）ときは、最初の A の来た画面を引き継ぐ。
 *   そうしないと A の ← が B へ行き、B の ← が A へ行く往復になる。
 *   ただしメニュー・下のタブなど外枠から移ったとき（`jump`）はかけない。人が選んで移ったので、
 *   ← はひとつ前の画面（B）にする（PRODUCT_DEV-1159）
 */
const resolveFrom = (
  store: NavStore,
  prev: NavEntry | undefined,
  identity: string,
  jump: boolean,
): string | null => {
  if (!prev) return null;
  let from = prev.identity === identity ? prev.from : prev.id;
  from = skipTransient(store, from);
  // A → 作成中・録音中 → A と戻ってきた: A の来た画面を引き継ぐ
  if (from && store.entries[from]?.identity === identity) from = store.entries[from].from;
  if (from && !jump) {
    const parent = store.entries[from];
    const grand = parent?.from ? store.entries[parent.from] : undefined;
    if (grand && grand.identity === identity) from = skipTransient(store, grand.from);
  }
  return from;
};

const prune = (store: NavStore): NavStore => {
  const ids = Object.keys(store.entries);
  if (ids.length <= NAV_STORE_LIMIT) return store;
  const keep = new Set(
    ids
      .sort((a, b) => store.entries[b].seq - store.entries[a].seq)
      .slice(0, NAV_STORE_LIMIT),
  );
  const entries: Record<string, NavEntry> = {};
  for (const id of keep) entries[id] = store.entries[id];
  const keys: Record<string, string> = {};
  for (const [k, id] of Object.entries(store.keys)) if (keep.has(id)) keys[k] = id;
  return { ...store, entries, keys };
};

export const reduceNav = (store: NavStore, ev: NavEvent): NavStore => {
  const identity = identityOf(ev.handle, ev.pathname);
  const screenKey = ev.handle?.screen ?? null;
  const transient = ev.handle?.transient === true;
  const prevId = ev.prevKey ? store.keys[ev.prevKey] : undefined;
  const prev = prevId ? store.entries[prevId] : undefined;

  // 履歴を移動した（戻る・進む・再読み込み）: 知っている枠ならそれを今の枠にする
  if (ev.action === "POP") {
    const known = store.keys[ev.key];
    if (known && store.entries[known]) {
      const was = store.entries[known];
      const entry: NavEntry = { ...was, url: ev.url, idx: ev.idx ?? was.idx };
      // 読み込み直しの前に記録した枠（`fullPageNavigate`）は、画面の種類をここで埋める
      if (!was.screen && screenKey) {
        entry.screen = screenKey;
        entry.identity = identity;
        entry.transient = was.transient || transient;
      }
      return { ...store, entries: { ...store.entries, [known]: entry } };
    }
  }

  // 戻る操作の置き換え: 置き換え先は来た画面の枠そのもの
  if (ev.adopt && store.entries[ev.adopt]) {
    const target = store.entries[ev.adopt];
    const entry: NavEntry = { ...target, url: ev.url, idx: ev.idx, doc: ev.doc };
    return {
      ...store,
      entries: { ...store.entries, [target.id]: entry },
      keys: { ...store.keys, [ev.key]: target.id },
    };
  }

  // 置き換え（保存後の遷移・リダイレクト・絞り込みの変更）: 同じ枠のまま中身を差し替える
  if (ev.action === "REPLACE" && prev) {
    const sameScreen = prev.identity === identity;
    // 来た画面と同じ画面へ置き換わった（編集できず詳細へ戻された、など）: その画面の来た画面を引き継ぐ。
    // そのままだと ← が同じ画面へ戻る
    const fromEntry = prev.from ? store.entries[prev.from] : undefined;
    const from =
      fromEntry && fromEntry.identity === identity ? skipTransient(store, fromEntry.from) : prev.from;
    const entry: NavEntry = {
      ...prev,
      from,
      url: ev.url,
      idx: ev.idx ?? prev.idx,
      doc: ev.doc,
      identity,
      screen: screenKey,
      transient,
      title: sameScreen ? prev.title : undefined,
    };
    return {
      ...store,
      entries: { ...store.entries, [prev.id]: entry },
      keys: { ...store.keys, [ev.key]: prev.id },
    };
  }

  // 進んだ（または知らない枠に着いた）: 新しい枠を作る
  const seq = store.seq + 1;
  const id = `e${seq}`;
  const entry: NavEntry = {
    id,
    doc: ev.doc,
    idx: ev.idx,
    url: ev.url,
    identity,
    screen: screenKey,
    transient,
    from: ev.action === "POP" ? null : resolveFrom(store, prev, identity, ev.jump === true),
    seq,
  };
  return prune({
    entries: { ...store.entries, [id]: entry },
    keys: { ...store.keys, [ev.key]: id },
    seq,
  });
};

export const setEntryTitle = (store: NavStore, key: string, title: string): NavStore => {
  const id = store.keys[key];
  const entry = id ? store.entries[id] : undefined;
  if (!entry || entry.title === title) return store;
  return { ...store, entries: { ...store.entries, [id]: { ...entry, title } } };
};

export const setEntryOverride = (
  store: NavStore,
  key: string,
  override: ScreenOverride | null,
): NavStore => {
  const id = store.keys[key];
  const entry = id ? store.entries[id] : undefined;
  if (!entry) return store;
  const cur = entry.override;
  if (!override && !cur) return store;
  if (
    override &&
    cur &&
    cur.url === override.url &&
    cur.title === override.title &&
    cur.closeToOrigin === override.closeToOrigin
  )
    return store;
  const next: NavEntry = { ...entry };
  if (override) next.override = override;
  else delete next.override;
  return { ...store, entries: { ...store.entries, [id]: next } };
};

/** 画面の中の状態で一時的な画面になった（移動先を選んでいる商談一覧、など）。戻り先にしない */
export const setEntryTransient = (store: NavStore, key: string, transient: boolean): NavStore => {
  const id = store.keys[key];
  const entry = id ? store.entries[id] : undefined;
  if (!entry || entry.transient === transient) return store;
  return { ...store, entries: { ...store.entries, [id]: { ...entry, transient } } };
};

export type BackTarget =
  | { kind: "none" }
  | { kind: "history"; delta: number; target: NavEntry }
  | { kind: "url"; url: string; target: NavEntry };

/** 今の画面の ← の行き先 */
export const resolveBack = (store: NavStore, key: string): BackTarget => {
  const curId = store.keys[key];
  const cur = curId ? store.entries[curId] : undefined;
  const from = cur?.from ? store.entries[cur.from] : undefined;
  if (!cur || !from) return { kind: "none" };
  if (from.doc === cur.doc && from.idx != null && cur.idx != null && from.idx < cur.idx) {
    return { kind: "history", delta: from.idx - cur.idx, target: from };
  }
  return { kind: "url", url: from.override?.url ?? from.url, target: from };
};

export const currentEntry = (store: NavStore, key: string): NavEntry | undefined => {
  const id = store.keys[key];
  return id ? store.entries[id] : undefined;
};

// ─────────────────────────────────────────────────────────────
// ブラウザと router につなぐ部分
// ─────────────────────────────────────────────────────────────

const STORE_KEY = "unizo.nav.v1";
const DOC_KEY = "unizo.nav.doc";
/** history.state に足す印。react-router は usr / key / idx しか読まないので共存できる */
const DOC_FIELD = "__navDoc";

const newId = (): string => Math.random().toString(36).slice(2, 10);

const loadStore = (): NavStore => {
  try {
    const raw = window.sessionStorage.getItem(STORE_KEY);
    if (!raw) return emptyNavStore();
    const parsed = JSON.parse(raw) as NavStore;
    if (!parsed || typeof parsed !== "object" || !parsed.entries || !parsed.keys) return emptyNavStore();
    return {
      ...(typeof parsed.owner === "string" ? { owner: parsed.owner } : {}),
      entries: parsed.entries,
      keys: parsed.keys,
      seq: Number(parsed.seq) || 0,
    };
  } catch {
    return emptyNavStore();
  }
};

const saveStore = (store: NavStore): void => {
  try {
    window.sessionStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    // 覚えられないだけ（← は来た画面が分からずホームへ行く）
  }
};

let store: NavStore = emptyNavStore();
let doc = "";
let lastKey: string | null = null;
let installed = false;
const listeners = new Set<() => void>();

interface PendingBack {
  /** 履歴を戻ったときに着くはずの枠 */
  expect: string;
  /** 着かなかったときに URL で戻る先 */
  fallbackUrl: string;
  /** 着いたあと location.state に載せ直すもの（保存後の通知など） */
  state?: unknown;
}
let pending: PendingBack | null = null;
let pendingAdopt: string | null = null;

/** 新しい文書（URL 直打ち・通知リンク・window.location での遷移）では key がどれも "default" になるので印で分ける */
const normalizeKey = (key: string): string => (key === "default" ? `default@${doc}` : key);

const readIdx = (): number | null => {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" ? idx : null;
};

/**
 * router を作る前に呼ぶ。履歴の番号が振り直される（新しい文書）かどうかはここでしか分からない
 * （router を作った時点で react-router が idx: 0 を書き込むため）。
 */
export const prepareHistory = (): void => {
  try {
    store = loadStore();
    const st = window.history.state as Record<string, unknown> | null;
    if (st?.idx == null) {
      doc = newId();
      window.history.replaceState({ ...(st ?? {}), [DOC_FIELD]: doc }, "");
    } else {
      doc =
        (typeof st[DOC_FIELD] === "string" ? (st[DOC_FIELD] as string) : null) ??
        window.sessionStorage.getItem(DOC_KEY) ??
        newId();
    }
    window.sessionStorage.setItem(DOC_KEY, doc);
  } catch {
    doc = doc || newId();
  }
};

interface RouterLike {
  state: {
    historyAction: string;
    location: { key: string; pathname: string; search: string; hash: string; state?: unknown };
    matches: ReadonlyArray<{ route: { handle?: unknown } }>;
  };
  subscribe: (fn: (state: RouterLike["state"]) => void) => () => void;
  navigate: (to: string, opts?: { replace?: boolean; state?: unknown }) => unknown;
  basename?: string;
}

const handleOf = (
  matches: RouterLike["state"]["matches"],
  read: (h: unknown) => ScreenHandle | null,
): ScreenHandle | null => {
  for (let i = matches.length - 1; i >= 0; i--) {
    const h = read(matches[i].route.handle);
    if (h) return h;
  }
  return null;
};

const urlOf = (loc: RouterLike["state"]["location"], basename: string): string => {
  const base = basename === "/" ? "" : basename.replace(/\/$/, "");
  return `${base}${loc.pathname}${loc.search}${loc.hash}`;
};

/**
 * router に記録をつなぐ。`router.subscribe` は React の描画より前に呼ばれるので、
 * 着いた画面が最初に描画される時点で記録は更新済み（← の ○○ が古い値にならない）。
 */
export const installBackTracker = (
  router: RouterLike,
  read: (h: unknown) => ScreenHandle | null,
  basename: string,
): void => {
  if (installed) return;
  installed = true;
  if (!doc) prepareHistory();

  const record = (state: RouterLike["state"], first: boolean) => {
    const loc = state.location;
    const key = normalizeKey(loc.key);
    if (!first && key === lastKey) return;
    const action = (first ? "POP" : state.historyAction) as NavEvent["action"];
    const adopt = action === "REPLACE" ? pendingAdopt : null;
    pendingAdopt = null;
    store = reduceNav(store, {
      action,
      prevKey: first ? null : lastKey,
      key,
      doc,
      idx: readIdx(),
      url: urlOf(loc, basename),
      pathname: loc.pathname,
      handle: handleOf(state.matches, read),
      ...(adopt ? { adopt } : {}),
      ...(action === "PUSH" && isNavJump(loc.state) ? { jump: true } : {}),
    });
    lastKey = key;
    // ← で履歴を戻る途中に、別の遷移（下のタブなど）が割り込んだ: 戻ったあとの確認はもう要らない
    if (action !== "POP") pending = null;
    const landed = currentEntry(store, key);
    if (action === "POP" && landed) doc = landed.doc;
    try {
      window.sessionStorage.setItem(DOC_KEY, doc);
    } catch {
      // 印を残せないだけ
    }
    saveStore(store);
    listeners.forEach((fn) => fn());

    // ← で履歴を戻ったあとの確認
    if (pending && action === "POP") {
      const p = pending;
      pending = null;
      if (!landed || landed.id !== p.expect) {
        pendingAdopt = p.expect;
        setTimeout(() => router.navigate(p.fallbackUrl, { replace: true, state: p.state }), 0);
      } else if (landed.override || p.state !== undefined) {
        // 画面の中で開いていた状態（検索など）へ戻す・保存後の通知を載せる
        const to = landed.override?.url ?? urlOf(loc, basename);
        setTimeout(() => router.navigate(to, { replace: true, state: p.state }), 0);
      }
    }
  };

  record(router.state, true);
  router.subscribe((state) => record(state, false));
};

/**
 * ログインした人を記録に結び付ける。前に記録した人と違えば、記録を捨てて true を返す
 * （呼び出し側は、枠ごとに覚えている別の記憶（手帳の見ていた日）も捨てる）
 */
export const bindNavOwner = (owner: string): boolean => {
  if (!owner || store.owner === owner) return false;
  const fresh = !store.owner;
  store = fresh ? { ...store, owner } : { ...emptyNavStore(), owner };
  if (!fresh) {
    lastKey = null;
    pending = null;
    pendingAdopt = null;
    listeners.forEach((fn) => fn());
  }
  saveStore(store);
  return !fresh;
};

/** ログアウトのとき、来た画面の記録を消す（商談名・顧客名・検索語が入っているため） */
export const clearNavMemory = (): void => {
  store = emptyNavStore();
  pending = null;
  pendingAdopt = null;
  try {
    window.sessionStorage.removeItem(STORE_KEY);
    window.sessionStorage.removeItem(DOC_KEY);
  } catch {
    // 消せないだけ（次にログインした人と違えば bindNavOwner で捨てる）
  }
};

/**
 * ページを読み込み直して移る（同じ画面を作り直す必要があるとき）。読み込み直しても来た画面が
 * 分かるよう、移る先の枠を先に記録し、履歴の番号と印を付けてから読み込み直す。
 * 記録が動いていないとき・失敗したときは、今までどおり `location.href` で移る。
 */
export const fullPageNavigate = (url: string, opts: { replace?: boolean } = {}): void => {
  try {
    if (!installed || !lastKey) throw new Error("not installed");
    const cur = readIdx();
    if (cur == null) throw new Error("no idx");
    const idx = opts.replace ? cur : cur + 1;
    const key = newId();
    const parsed = new URL(url, window.location.origin);
    store = reduceNav(store, {
      action: opts.replace ? "REPLACE" : "PUSH",
      prevKey: lastKey,
      key,
      doc,
      idx,
      url: `${parsed.pathname}${parsed.search}${parsed.hash}`,
      pathname: parsed.pathname,
      // 画面の種類は読み込み直したあとに埋める（POP で知っている枠に着いたとき）
      handle: null,
    });
    saveStore(store);
    const state = { usr: null, key, idx, [DOC_FIELD]: doc };
    if (opts.replace) window.history.replaceState(state, "", url);
    else window.history.pushState(state, "", url);
    window.location.reload();
  } catch {
    if (opts.replace) window.location.replace(url);
    else window.location.href = url;
  }
};

export const getNavStore = (): NavStore => store;
/** 記録が動いているか（テストで画面だけを描くときは動いていない＝← は今までどおり） */
export const isBackTrackerInstalled = (): boolean => installed;
export const normalizeLocationKey = (key: string): string => normalizeKey(key);
export const subscribeNav = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** 今の画面のタイトルを記録する（この画面へ戻ってくるときの ← の ○○ になる） */
export const recordTitle = (locationKey: string, title: string): void => {
  const next = setEntryTitle(store, normalizeKey(locationKey), title);
  if (next !== store) {
    store = next;
    saveStore(store);
  }
};

/** 画面の中で開いている状態ぶんの「戻ってくるときの行き先と名前」を記録する（null で消す） */
export const recordOverride = (
  locationKey: string,
  override: ScreenOverride | null,
): void => {
  const next = setEntryOverride(store, normalizeKey(locationKey), override);
  if (next !== store) {
    store = next;
    saveStore(store);
  }
};

/** 今の画面に記録してある「画面の中の状態」 */
export const readOverride = (locationKey: string): ScreenOverride | undefined =>
  currentEntry(store, normalizeKey(locationKey))?.override;

/** 履歴を戻る前に、着いたあとの確認を仕込む */
export const expectBack = (p: PendingBack): void => {
  pending = p;
};

/** 今の画面を一時的な画面（戻り先にしない）にする・戻す */
export const recordTransient = (locationKey: string, transient: boolean): void => {
  const next = setEntryTransient(store, normalizeKey(locationKey), transient);
  if (next !== store) {
    store = next;
    saveStore(store);
  }
};

/** URL で戻るときに、置き換え先を来た画面の枠そのものとして扱わせる */
export const adoptOnNextReplace = (id: string): void => {
  pendingAdopt = id;
};
